import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DomainInput, GroupInput } from '@redirector/shared';
import { apiGet, apiSend } from '../lib/api.js';

export type GroupSummary = {
  id: number;
  name: string;
  notes: string;
  domainCount: number;
  entryCount: number;
  createdAt: string;
};

export type Group = { id: number; name: string; notes: string; createdAt: string; updatedAt: string };

export type Domain = {
  id: number;
  groupId: number;
  hostname: string;
  fallbackUrl: string | null;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
};

export const groupKeys = {
  all: ['groups'] as const,
  one: (id: number) => ['groups', id] as const,
};

export function useGroups() {
  return useQuery({
    queryKey: groupKeys.all,
    queryFn: () => apiGet<{ groups: GroupSummary[] }>('/api/groups'),
  });
}

export function useGroup(id: number) {
  return useQuery({
    queryKey: groupKeys.one(id),
    queryFn: () => apiGet<{ group: Group; domains: Domain[] }>(`/api/groups/${id}`),
  });
}

/** Every mutation invalidates the palette index too, so search stays truthful. */
function useInvalidate() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: groupKeys.all });
    void client.invalidateQueries({ queryKey: ['liveness'] });
  };
}

export function useCreateGroup() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: GroupInput) => apiSend<{ group: Group }>('POST', '/api/groups', input),
    onSuccess: invalidate,
  });
}

export function useUpdateGroup(id: number) {
  const client = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: GroupInput) =>
      apiSend<{ group: Group }>('PATCH', `/api/groups/${id}`, input),
    onSuccess: () => {
      invalidate();
      void client.invalidateQueries({ queryKey: groupKeys.one(id) });
    },
  });
}

export function useDeleteGroup() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: number) => apiSend<{ deleted: true }>('DELETE', `/api/groups/${id}`),
    onSuccess: invalidate,
  });
}

export function useCreateDomain(groupId: number) {
  const client = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: DomainInput) =>
      apiSend<{ domain: Domain }>('POST', `/api/groups/${groupId}/domains`, input),
    onSuccess: () => {
      invalidate();
      void client.invalidateQueries({ queryKey: groupKeys.one(groupId) });
    },
  });
}

export function useUpdateDomain(groupId: number) {
  const client = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (variables: { id: number; input: DomainInput }) =>
      apiSend<{ domain: Domain }>('PATCH', `/api/domains/${variables.id}`, variables.input),
    onSuccess: () => {
      invalidate();
      void client.invalidateQueries({ queryKey: groupKeys.one(groupId) });
    },
  });
}

export function useDeleteDomain(groupId: number) {
  const client = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: number) => apiSend<{ deleted: true }>('DELETE', `/api/domains/${id}`),
    onSuccess: () => {
      invalidate();
      void client.invalidateQueries({ queryKey: groupKeys.one(groupId) });
    },
  });
}
